import { useState } from 'react'
import { router } from '@inertiajs/react'
import Logo from '../../components/Logo'

/**
 * OTP verification interstitial — shown after password login for 2FA-enrolled users.
 * Accepts a 6-digit TOTP code or a backup code in XXXXX-XXXXX format.
 */
export default function OtpVerify({ flash }) {
  const [code, setCode] = useState('')
  const [useBackup, setUseBackup] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  function handleSubmit(e) {
    e.preventDefault()
    if (!code.trim()) return
    setSubmitting(true)
    router.post('/otp/verify', { code: code.trim() }, {
      onFinish: () => setSubmitting(false),
    })
  }

  function handleCodeChange(e) {
    const raw = e.target.value
    if (useBackup) {
      // Backup code: allow A-Z, 2-9, hyphen — auto-insert hyphen after 5 chars
      const clean = raw.toUpperCase().replace(/[^A-Z2-9]/g, '')
      if (clean.length <= 5) {
        setCode(clean)
      } else {
        setCode(clean.slice(0, 5) + '-' + clean.slice(5, 10))
      }
    } else {
      // TOTP: digits only, max 6
      setCode(raw.replace(/\D/g, '').slice(0, 6))
    }
  }

  return (
    <div className="min-h-screen bg-zinc-900 flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[500px] h-[500px] rounded-full bg-emerald-900/8 blur-3xl" />
      </div>

      <div className="w-full max-w-sm relative z-10">
        <div className="mb-10 flex flex-col items-center">
          <Logo variant="full" size={240} transparent />
        </div>

        {flash?.alert && (
          <div className="mb-4 px-4 py-2 bg-red-950 border border-red-800 rounded-lg text-sm text-red-400 font-mono">
            {flash.alert}
          </div>
        )}

        <div className="bg-zinc-800/30 border border-zinc-600/50 rounded-2xl p-6">
          <h2 className="text-zinc-200 font-mono text-sm font-semibold mb-1">
            Two-Factor Verification
          </h2>
          <p className="text-zinc-500 font-mono text-xs mb-5">
            {useBackup
              ? 'Enter a backup code (XXXXX-XXXXX format).'
              : 'Enter the 6-digit code from your authenticator app.'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[10px] font-mono text-zinc-500 mb-1.5 uppercase tracking-widest">
                {useBackup ? 'Backup Code' : 'Authentication Code'}
              </label>
              <input
                type="text"
                inputMode={useBackup ? 'text' : 'numeric'}
                value={code}
                onChange={handleCodeChange}
                placeholder={useBackup ? 'XXXXX-XXXXX' : '000000'}
                maxLength={useBackup ? 11 : 6}
                autoFocus
                autoComplete="one-time-code"
                className="w-full bg-zinc-800/60 border border-zinc-600/60 rounded-lg px-3 py-2.5 text-lg font-mono text-zinc-100 placeholder-zinc-600 text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-600 transition-colors"
              />
            </div>

            <button
              type="submit"
              disabled={submitting || code.length < (useBackup ? 11 : 6)}
              className="w-full bg-emerald-800/60 hover:bg-emerald-700/80 disabled:opacity-50 disabled:cursor-not-allowed text-emerald-200 border border-emerald-700/40 font-mono text-sm font-semibold py-2.5 px-4 rounded-lg transition-colors"
            >
              {submitting ? 'Verifying...' : 'Verify'}
            </button>
          </form>
        </div>

        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => { setCode(''); setUseBackup(b => !b) }}
            className="text-[11px] font-mono text-zinc-500 hover:text-zinc-300 transition-colors underline underline-offset-2"
          >
            {useBackup ? 'Use authenticator code instead' : 'Use a backup code instead'}
          </button>
        </div>
      </div>
    </div>
  )
}
